import { useState } from "react";
import { Input, Label } from "@tellescope/ui";
import { Tooltip } from "@tellescope/design-system";
import { slugify } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import type { Page } from "../../../shared/schema";
import type { VisibilityCondition } from "../../../shared/schema/visibility";
import { VisibilitySection } from "./BlockEditor/shared/VisibilitySection";

type PageEditorFields = Pick<Page, "title" | "slug" | "visibility">;

interface PageEditorProps {
  page: PageEditorFields;
  onUpdate: (updates: Partial<PageEditorFields>) => void;
}

export const PageEditor: React.FC<PageEditorProps> = ({ page, onUpdate }) => {
  const { t } = useTranslation();
  const [visibilityOpen, setVisibilityOpen] = useState(false);

  const handleTitleChange = (title: string) => {
    onUpdate({ title });
  };

  const handleSlugChange = (rawSlug: string) => {
    // Auto-format slug using slugify utility, preserving hyphens the user types
    const slug = slugify(rawSlug, { preserveSeparator: true });
    onUpdate({ slug });
  };

  const handleVisibilityChange = (condition: VisibilityCondition | undefined) => {
    onUpdate({ visibility: condition ? { condition } : undefined });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="page-title">{t("Page Title")}</Label>
          <Input
            id="page-title"
            value={page.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder={t("Enter page title")}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <Label htmlFor="page-slug">{t("URL Slug")}</Label>
            <Tooltip.Provider delayDuration={200}>
              <Tooltip>
                <Tooltip.Trigger asChild>
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </Tooltip.Trigger>
                <Tooltip.Content>
                  <p>{t("Lowercase letters, numbers, and hyphens only")}</p>
                </Tooltip.Content>
              </Tooltip>
            </Tooltip.Provider>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">/</span>
            <Input
              id="page-slug"
              value={page.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder={t("page-url")}
              className="font-mono text-sm"
            />
          </div>
        </div>
      </div>

      <VisibilitySection
        visibility={page.visibility}
        onChange={handleVisibilityChange}
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
        label={t("Page Visibility Conditions")}
        description={t("Set conditions to show or hide this entire page based on patient data.")}
      />
    </div>
  );
};
